import { Activity, Calculator, GitBranch, MapPin, Radar, Scale, ShieldAlert } from "lucide-react";
import { useLocale, useTranslation, geographyText } from "@ncp/i18n";
import { Badge, Card, RiskGauge, StackedBars, cn } from "@ncp/ui";
import { deltaText, fmtAed, hhiText, type EvidencePack, type MergerSimulationResult, type StudyAnalysis } from "./api";
import { useThresholdFigures } from "../case/figures";
import { MarketGraphSvg } from "./MarketGraphView";
import { thresholdWords, useCalculationMethod, type MeasureKey } from "../case/figures";
import { FigureInfo } from "../case/MethodInfo";
import { DominanceBasis } from "./DominanceBasis";

function Panel({ title, method, icon: Icon, children, tone = "gold", measure }: { title: string; method?: string; icon: typeof Scale; children: React.ReactNode; tone?: "gold" | "danger"; measure?: MeasureKey }) {
  return (
    <Card className="p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className={cn("grid size-9 shrink-0 place-items-center rounded-lg", tone === "danger" ? "bg-danger-50 text-danger-700" : "bg-aegold-50 text-aegold-800")}><Icon className="size-4" /></span>
        <div className="min-w-0">
          <h4 className="inline-flex items-center gap-1 text-base font-semibold text-aeblack-950">{title}{measure ? <FigureInfo measure={measure} /> : null}</h4>
          {method ? <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{method}</p> : null}
        </div>
      </div>
      {children}
    </Card>
  );
}

function Reading({ text }: { text?: string | null }) {
  const { t } = useTranslation();
  if (!text) return null;
  return (
    <div className="mt-4 flex gap-3 rounded-xl bg-sand-50 p-3">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-aegold-800">{t("backoffice:dataBank.intel.reading")}</span>
      <p className="text-sm leading-relaxed text-aeblack-900/90">{text}</p>
    </div>
  );
}

const pct = (v: number | null | undefined, digits = 1) => (v == null ? "—" : `${v.toLocaleString(undefined, { maximumFractionDigits: digits })}%`);
const signed = (v: number, digits = 2) => `${v > 0 ? "+" : ""}${v.toLocaleString(undefined, { maximumFractionDigits: digits })}%`;

function SimulationTable({ sim, remedy, hhiPre }: { sim: MergerSimulationResult; remedy?: MergerSimulationResult | null; hhiPre: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const rows: Array<{ label: string; hhi: number; delta: string; price: string; cs: string; aed: string; tone?: string }> = [
    { label: t("backoffice:dataBank.intel.preMerger"), hhi: hhiPre, delta: "—", price: "—", cs: "—", aed: "—" },
    { label: sim.scenario, hhi: sim.hhiPost, delta: `+${deltaText(sim.deltaHhi, locale)}`, price: signed(sim.shareWeightedPriceChangePercent), cs: signed(sim.consumerSurplusChangePercent), aed: sim.annualPriceEffectAed ? `${fmtAed(sim.annualPriceEffectAed, locale)}` : "—", tone: "danger" },
  ];
  if (remedy) rows.push({ label: remedy.scenario, hhi: remedy.hhiPost, delta: `${remedy.deltaHhi > 0 ? "+" : ""}${deltaText(remedy.deltaHhi, locale)}`, price: signed(remedy.shareWeightedPriceChangePercent), cs: signed(remedy.consumerSurplusChangePercent), aed: remedy.annualPriceEffectAed ? `${fmtAed(remedy.annualPriceEffectAed, locale)}` : "—", tone: "warning" });
  return (
    <div className="overflow-x-auto" tabIndex={0}>
      <table className="w-full text-xs">
        <thead><tr className="border-b border-sand-200 bg-sand-50 text-start text-xs font-medium text-sand-700">
          <th className="p-2 text-start">{t("backoffice:dataBank.intel.scenario")}</th><th className="p-2 text-end">HHI</th><th className="p-2 text-end">Δ HHI</th><th className="p-2 text-end">{t("backoffice:dataBank.intel.priceEffect")}</th><th className="p-2 text-end">{t("backoffice:dataBank.intel.consumerSurplus")}</th><th className="p-2 text-end">{t("backoffice:dataBank.intel.annualEffect")}</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-sand-100">
              <td className="p-2 text-aeblack-950">{r.label}</td>
              <td className="p-2 text-end tabular">{hhiText(r.hhi, locale)}</td>
              <td className="p-2 text-end tabular">{r.delta}</td>
              <td className={cn("p-2 text-end tabular font-semibold", r.tone === "danger" ? "text-danger-700" : r.tone === "warning" ? "text-warning-700" : "")}>{r.price}</td>
              <td className="p-2 text-end tabular">{r.cs}</td>
              <td className="p-2 text-end tabular">{r.aed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The deterministic evidence pack of a study (modes 1–3), with the agent's readings beside each instrument. */
export function StudyEvidencePanels({ evidence, analysis }: { evidence: EvidencePack; analysis?: StudyAnalysis | null }) {
  const marks = useThresholdFigures();
  const { t } = useTranslation();
  const { locale } = useLocale();
  // Condition 2.14: the dominance threshold and GUPPI marks named here are the published method's.
  const words = thresholdWords(useCalculationMethod().data?.bands, locale);
  const e = evidence;
  const readings = (analysis as (StudyAnalysis & { evidence?: Record<string, string> }) | null | undefined)?.evidence ?? {};
  const hmt = e.hypotheticalMonopolist ?? [];
  const sc = e.screening;

  return (
    <div className="space-y-5">
      <Panel title={t("backoffice:dataBank.intel.hmt")} method={hmt[0]?.method} icon={Scale}>
        <div className="grid gap-3 sm:grid-cols-2">
          {hmt.map((h) => {
            const decided = h.determined !== false;
            return (
            <div key={h.ssnipPercent} className={cn("rounded-xl border p-4", !decided ? "border-sand-200 bg-sand-50/60" : h.passesAsDistinctMarket ? "border-success-200 bg-success-50/40" : "border-warning-200 bg-warning-50/40")}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-aeblack-950">SSNIP {h.ssnipPercent}%</p>
                <Badge variant={!decided ? "outline" : h.passesAsDistinctMarket ? "success" : "warning"} size="sm" dot>
                  {!decided ? t("backoffice:dataBank.intel.undecidedMarket") : h.passesAsDistinctMarket ? t("backoffice:dataBank.intel.distinctMarket") : t("backoffice:dataBank.intel.widenMarket")}
                </Badge>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <dt className="text-sand-700">{t("backoffice:dataBank.intel.criticalLoss")}</dt><dd className="tabular text-end font-semibold">{pct(h.criticalLossPercent, 2)}</dd>
                {decided ? (
                  <><dt className="text-sand-700">{t("backoffice:dataBank.intel.actualLoss")}</dt><dd className="tabular text-end font-semibold">{pct(h.actualLossPercent, 2)}</dd></>
                ) : (
                  <><dt className="text-sand-700">{t("backoffice:dataBank.intel.breakEvenRecapture")}</dt><dd className="tabular text-end font-semibold">{pct(h.breakEvenRecapturePercent ?? h.criticalLossPercent, 2)}</dd></>
                )}
                <dt className="text-sand-700">{t("backoffice:dataBank.intel.margin")}</dt><dd className="tabular text-end">{pct(h.marginPercent)}</dd>
                <dt className="text-sand-700">{t("backoffice:dataBank.intel.elasticity")}</dt><dd className="tabular text-end">{h.impliedMarketElasticity.toFixed(3)}</dd>
              </dl>
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{h.interpretation}</p>
            </div>
            );
          })}
        </div>
        <Reading text={readings.hypotheticalMonopolist} />
      </Panel>

      {e.pricingPressure ? (
        <Panel title={`${t("backoffice:dataBank.intel.pricingPressure")} — ${e.pricingPressure.product1} + ${e.pricingPressure.product2}`} method={e.pricingPressure.method} icon={Calculator}>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: "GUPPI", a: e.pricingPressure.guppi1Percent, b: e.pricingPressure.guppi2Percent, hint: t("backoffice:dataBank.intel.guppiHint") },
              { label: "CMCR", a: e.pricingPressure.cmcr1Percent, b: e.pricingPressure.cmcr2Percent, hint: t("backoffice:dataBank.intel.cmcrHint") },
              { label: t("backoffice:dataBank.intel.netUpp"), a: e.pricingPressure.netUpp1Percent, b: e.pricingPressure.netUpp2Percent, hint: t("backoffice:dataBank.intel.uppHint", { credit: e.pricingPressure.efficiencyCreditPercent }) },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-sand-200 p-4">
                <p className="text-xs font-medium text-sand-700">{m.label}</p>
                <p className={cn("mt-1 font-heading text-2xl font-semibold tabular", m.label === "GUPPI" && Math.max(m.a, m.b) >= 10 ? "text-danger-700" : "text-aeblack-950")}>{pct(m.a)} <span className="text-base text-muted-foreground">/ {pct(m.b)}</span></p>
                <p className="mt-1 text-[11px] text-muted-foreground">{m.hint}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("backoffice:dataBank.intel.diversion")}: {pct(e.pricingPressure.diversion12Percent)} → / {pct(e.pricingPressure.diversion21Percent)} ← · {e.pricingPressure.reading}</p>
          <Reading text={readings.pricingPressure} />
        </Panel>
      ) : null}

      {e.simulation ? (
        <Panel title={t("backoffice:dataBank.intel.simulation")} method={e.simulation.method} icon={Activity} measure="mergerSimulation">
          <SimulationTable sim={e.simulation} remedy={e.simulationWithRemedy} hhiPre={e.simulation.hhiPre} />
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium text-sand-700">{t("backoffice:dataBank.intel.priceChangeBy")}</p>
            <StackedBars categories={e.simulation.products.map((p) => p.product)} series={[{ name: t("backoffice:dataBank.intel.priceEffect"), data: e.simulation.products.map((p) => p.priceChangePercent) }]} height={Math.max(200, 34 * e.simulation.products.length)} />
          </div>
          <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">{e.simulation.assumptions.map((a) => <li key={a}>• {a}</li>)}</ul>
          <Reading text={readings.simulation} />
        </Panel>
      ) : null}

      {sc || e.priceParallelism || e.creepingAcquisitions.length ? (
        <Panel title={t("backoffice:dataBank.intel.screens")} method={sc?.method} icon={Radar} tone={sc && sc.compositeScore >= 70 ? "danger" : "gold"}>
          {sc ? (
            <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
              <div className="flex flex-col items-center"><RiskGauge score={sc.compositeScore} size={160} label={t("backoffice:dataBank.intel.composite")} /><p className="mt-1 text-[11px] text-muted-foreground">{sc.tenders} {t("backoffice:dataBank.intel.tenders")} · {sc.bids} {t("backoffice:dataBank.intel.bids")}</p></div>
              <div>
                <p className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.intel.redFlags")}</p>
                <ul className="mt-1 space-y-1.5">{sc.redFlags.length === 0 ? <li className="text-xs text-muted-foreground">{t("backoffice:dataBank.intel.noFlags")}</li> : sc.redFlags.map((f) => <li key={f} className="flex gap-2 text-xs leading-relaxed"><ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-danger-600" />{f}</li>)}</ul>
                <p className="mt-3 text-[11px] text-muted-foreground">{t("backoffice:dataBank.intel.rotation")}: {pct(sc.rotation.rotationIndexPercent, 0)} · {t("backoffice:dataBank.intel.stability")}: {pct(sc.rotation.bidderSetStabilityPercent, 0)} · {t("backoffice:dataBank.intel.avgBidders")}: {sc.rotation.averageBiddersFirstHalf} → {sc.rotation.averageBiddersSecondHalf} · {t("backoffice:dataBank.intel.forensics")}: {sc.forensics.length}</p>
              </div>
            </div>
          ) : null}
          {e.priceParallelism ? <p className="mt-3 rounded-xl border border-sand-200 p-3 text-xs leading-relaxed"><span className="font-semibold">{t("backoffice:dataBank.intel.priceVsBenchmark")}:</span> {e.priceParallelism.reading}</p> : null}
          {e.creepingAcquisitions.filter((c) => c.flag).map((c) => <p key={c.acquirer} className="mt-2 rounded-xl border border-warning-200 bg-warning-50/40 p-3 text-xs leading-relaxed"><span className="font-semibold">{t("backoffice:dataBank.intel.creeping")}:</span> {c.reading}</p>)}
          {sc ? <p className="mt-3 text-[11px] text-muted-foreground"><span className="font-semibold">{t("backoffice:dataBank.intel.innocent")}:</span> {sc.innocentExplanationsToExclude.join(" · ")}</p> : null}
          <Reading text={readings.screening} />
        </Panel>
      ) : null}

      {e.graph && e.graph.nodes.length > 1 ? (
        <Panel title={t("backoffice:dataBank.intel.graph")} method={e.graph.method} icon={GitBranch}>
          <MarketGraphSvg graph={e.graph} />
          {e.graph.findings.length > 0 ? (
            <ul className="mt-4 divide-y divide-sand-100">
              {e.graph.findings.map((f) => (
                <li key={f.title} className="flex gap-3 py-2.5">
                  <Badge variant={f.points >= 25 ? "danger" : "warning"} size="sm" className="shrink-0">{t(`backoffice:dataBank.intel.findingKind.${f.kind}`, { defaultValue: f.kind })}</Badge>
                  <div><p className="text-sm font-medium text-aeblack-950">{f.title}</p><p className="text-xs leading-relaxed text-muted-foreground">{f.detail}</p></div>
                </li>
              ))}
            </ul>
          ) : null}
          <Reading text={readings.ownershipAndAgencies} />
        </Panel>
      ) : null}

      {e.catchments.length > 0 ? (
        <Panel title={t("backoffice:dataBank.intel.catchments", { km: e.catchments[0]!.radiusKm })} method={e.catchments[0]!.method} icon={MapPin} measure="localCatchment">
          <div className="overflow-x-auto" tabIndex={0}>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-sand-200 bg-sand-50 text-xs font-medium text-sand-700">
                <th className="p-2 text-start">{t("backoffice:dataBank.intel.centre")}</th><th className="p-2 text-start">{t("backoffice:dataBank.emirate")}</th><th className="p-2 text-end">{t("backoffice:dataBank.intel.facilities")}</th><th className="p-2 text-start">{t("backoffice:dataBank.analysis.leader")}</th><th className="p-2 text-end">HHI</th><th className="p-2 text-end">{t("backoffice:dataBank.intel.ifLeadersCombine")}</th>
              </tr></thead>
              <tbody>
                {e.catchments.slice(0, 10).map((c) => (
                  <tr key={c.centre} className="border-b border-sand-100">
                    <td className="p-2">{c.centre}</td><td className="p-2 text-muted-foreground">{geographyText(c.emirate, locale)}</td><td className="p-2 text-end tabular">{c.facilitiesInside}</td>
                    <td className="p-2">{c.leader} <span className={cn("tabular font-semibold", c.dominancePresumption ? "text-danger-700" : "")}>{marks.share(c.leaderSharePercent)}</span></td>
                    <td className="p-2 text-end tabular">{hhiText(c.hhi, locale)}</td>
                    <td className="p-2 text-end tabular">{c.hhiPostMerger != null ? `${hhiText(c.hhiPostMerger, locale)} · ${marks.share(c.combinedSharePostMergerPercent)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DominanceBasis className="mt-2" />
          <Reading text={readings.catchments} />
        </Panel>
      ) : null}

      {e.reconciliation.length > 0 ? (
        <Panel title={t("backoffice:dataBank.intel.reconciliation")} method={e.reconciliation[0]!.corridor.method} icon={Calculator}>
          <ul className="divide-y divide-sand-100">
            {e.reconciliation.map((r) => (
              <li key={r.entityId} className="grid gap-2 py-2.5 md:grid-cols-[minmax(0,1fr)_120px_90px_170px]">
                <p className="text-sm text-aeblack-950">{r.entity}</p>
                <p className="text-xs tabular text-muted-foreground">{pct(r.corridor.lowPercent)} – {pct(r.corridor.highPercent)}</p>
                <p className="text-xs tabular font-semibold">{pct(r.corridor.pointEstimatePercent)}</p>
                <div className="flex flex-wrap gap-1">
                  {r.corridor.disputed ? <Badge variant="danger" size="sm">{t("backoffice:dataBank.intel.disputed")}</Badge> : <Badge variant="success" size="sm">{t("backoffice:dataBank.intel.agreed")}</Badge>}
                  {r.corridor.straddlesDominanceThreshold ? <Badge variant="warning" size="sm">{t("backoffice:dataBank.intel.straddles", words)}</Badge> : null}
                </div>
              </li>
            ))}
          </ul>
          {e.reconciliation.some((r) => r.corridor.straddlesDominanceThreshold) ? <DominanceBasis className="mt-2" /> : null}
          <Reading text={readings.dataQuality} />
        </Panel>
      ) : null}
    </div>
  );
}
